/**
 * Maps file extensions to icon names for record files.
 * Mirrors the logic from uploader-frontend's getRecordIcon + getFileTypeIcon.
 */

import {
  ICON_PDF,
  ICON_IMAGE_LINE,
  ICON_VIDEO,
  ICON_ZIP,
  ICON_NOTE,
  ICON_TXT,
  ICON_XML,
  ICON_PATHOLOGY,
  ICON_CCDA,
  ICON_DICOM,
  ICON_FILES,
} from '../refs/Icons';

// Record class constants (same values as uploader-frontend/enums/RecordClass)
export const RECORD_CLASS_PATHOLOGY = 'Pathology';
export const RECORD_CLASS_RADIOLOGY = 'Radiology';
export const RECORD_CLASS_CLINICAL  = 'Clinical';
export const RECORD_CLASS_OTHER     = 'Other';

export type RecordClass =
  | typeof RECORD_CLASS_PATHOLOGY
  | typeof RECORD_CLASS_RADIOLOGY
  | typeof RECORD_CLASS_CLINICAL
  | typeof RECORD_CLASS_OTHER
  | string;

const FILE_EXTENSION_TO_ICON: Record<string, string> = {
  'pdf': ICON_PDF,
  'heic': ICON_IMAGE_LINE,
  'jpg': ICON_IMAGE_LINE,
  'jpeg': ICON_IMAGE_LINE,
  'png': ICON_IMAGE_LINE,
  'gif': ICON_IMAGE_LINE,
  'bmp': ICON_IMAGE_LINE,
  'webp': ICON_IMAGE_LINE,
  'webm': ICON_VIDEO,
  'mpeg': ICON_VIDEO,
  'avi': ICON_VIDEO,
  'ogg': ICON_VIDEO,
  'mpg': ICON_VIDEO,
  'mp4': ICON_VIDEO,
  'mov': ICON_VIDEO,
  'm4a': ICON_VIDEO,
  'wmv': ICON_VIDEO,
  'ogv': ICON_VIDEO,
  'zip': ICON_ZIP,
  'gz': ICON_ZIP,
  'xz': ICON_ZIP,
  'bz': ICON_ZIP,
  'bz2': ICON_ZIP,
  '7z': ICON_ZIP,
  's7z': ICON_ZIP,
  'rar': ICON_ZIP,
  'doc': ICON_NOTE,
  'docx': ICON_NOTE,
  'rtf': ICON_NOTE,
  'odt': ICON_NOTE,
  'tif': ICON_PATHOLOGY,
  'tiff': ICON_PATHOLOGY,
  'bif': ICON_PATHOLOGY,
  'scn': ICON_PATHOLOGY,
  'svslide': ICON_PATHOLOGY,
  'svs': ICON_PATHOLOGY,
  'vms': ICON_PATHOLOGY,
  'mvu': ICON_PATHOLOGY,
  'isyntax': ICON_PATHOLOGY,
  'ndpi': ICON_PATHOLOGY,
  'txt': ICON_TXT,
  'xml': ICON_XML,
  'cda': ICON_CCDA,
  'ccda': ICON_CCDA,
  'dcm': ICON_DICOM,
  'dicom': ICON_DICOM,
};

const CLINICAL_CCDA_EXTENSIONS = ['xml', 'ccda', 'cda'];

/**
 * Extracts the file extension from a filename
 */
export const getFileExtension = (fileName: string): string => {
  if (!fileName) return '';
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

/**
 * Returns the appropriate icon name for a record, using the same resolution
 * priority as uploader-frontend's getRecordIcon:
 *
 * 1. recordClass === 'Pathology' → pathology icon
 * 2. recordClass === 'Radiology' → dicom icon
 * 3. recordClass === 'Clinical' + xml/ccda/cda extension → ccda icon
 * 4. Fall back to file extension lookup
 * 5. Default → generic file icon
 */
export const getRecordFileIcon = (
  fileName: string,
  recordClass?: RecordClass,
  fileExtension?: string,
): string => {
    
  // Priority 1: Pathology class always wins
  if (recordClass === RECORD_CLASS_PATHOLOGY) {
    return ICON_PATHOLOGY;
  }

  // Priority 2: Radiology class → DICOM icon
  if (recordClass === RECORD_CLASS_RADIOLOGY) {
    return ICON_DICOM;
  }

  // Use explicit fileExtension if provided, otherwise extract from fileName
  const ext = fileExtension
    ? fileExtension.toLowerCase()
    : getFileExtension(fileName);

  // Priority 3: Clinical class + XML/CDA extension → CCDA icon
  if (recordClass === RECORD_CLASS_CLINICAL && CLINICAL_CCDA_EXTENSIONS.includes(ext)) {
    return ICON_CCDA;
  }

  // Priority 4: Extension-based lookup
  if (ext && FILE_EXTENSION_TO_ICON[ext]) {
    return FILE_EXTENSION_TO_ICON[ext];
  }

  // Priority 5: Default fallback
  return ICON_FILES;
};
